import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  ShoppingBag, Plus, Minus, Trash2, ArrowRight, ArrowLeft, Package,
} from 'lucide-react'
import { getInfo, fmtPreco } from './lojaApi'
import { useCarrinho } from './useCarrinho'
import LojaLayout from './LojaLayout'

export default function LojaCarrinho() {
  const { slug = '' } = useParams()
  const navigate = useNavigate()

  const { data: info } = useQuery({
    queryKey: ['loja-info', slug],
    queryFn: () => getInfo(slug),
    enabled: !!slug,
  })

  const itens = useCarrinho((s) => s.getItens(slug))
  const total = useCarrinho((s) => s.getTotal(slug))
  const totalItens = useCarrinho((s) => s.getTotalItens(slug))
  const atualizarQuantidade = useCarrinho((s) => s.atualizarQuantidade)
  const remover = useCarrinho((s) => s.remover)
  const limpar = useCarrinho((s) => s.limpar)

  return (
    <LojaLayout info={info || null} slug={slug}>
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <Link to={`/loja/${slug}`} className="btn-ghost text-sm mb-4 inline-flex">
          <ArrowLeft size={14} /> Continuar comprando
        </Link>

        <div className="flex items-baseline justify-between mb-4">
          <h1 className="text-2xl font-semibold text-ink-900 flex items-center gap-2">
            <ShoppingBag size={22} /> Carrinho
          </h1>
          {itens.length > 0 && (
            <div className="text-sm text-ink-500">
              {totalItens} {totalItens === 1 ? 'item' : 'itens'}
            </div>
          )}
        </div>

        {itens.length === 0 ? (
          <div className="card card-body text-center py-12 text-ink-500">
            <Package size={32} className="mx-auto mb-2 text-ink-300" />
            <div className="font-medium text-ink-700 mb-1">Carrinho vazio</div>
            <div className="text-xs mb-4">Adicione produtos ao carrinho para continuar.</div>
            <Link to={`/loja/${slug}`} className="btn-primary inline-flex">
              Ver catálogo
            </Link>
          </div>
        ) : (
          <>
            <div className="card">
              <div className="divide-y divide-ink-100">
                {itens.map((item) => (
                  <div key={item.sku} className="p-4 flex items-start gap-4">
                    {/* Imagem */}
                    <div className="w-20 h-20 bg-ink-50 rounded-md overflow-hidden flex-shrink-0">
                      {item.foto ? (
                        <img src={item.foto} alt={item.descricao} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-ink-300">
                          <Package size={24} />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-ink-900 line-clamp-2">{item.descricao}</div>
                      <div className="text-xs text-ink-500 font-mono mt-0.5">
                        {item.sku} · R$ {fmtPreco(item.preco)} / {item.unidade}
                      </div>

                      {/* Controle de quantidade */}
                      <div className="flex items-center gap-3 mt-3">
                        <div className="inline-flex items-center border border-ink-200 rounded-md overflow-hidden">
                          <button
                            type="button"
                            onClick={() => atualizarQuantidade(slug, item.sku, item.quantidade - 1)}
                            className="w-8 h-8 flex items-center justify-center hover:bg-ink-50 text-ink-600"
                          >
                            <Minus size={12} />
                          </button>
                          <div className="w-10 text-center text-sm font-mono tabular-nums">{item.quantidade}</div>
                          <button
                            type="button"
                            onClick={() => atualizarQuantidade(slug, item.sku, item.quantidade + 1)}
                            disabled={item.quantidade >= item.estoqueDisponivel}
                            className="w-8 h-8 flex items-center justify-center hover:bg-ink-50 text-ink-600 disabled:opacity-30"
                          >
                            <Plus size={12} />
                          </button>
                        </div>

                        <button
                          type="button"
                          onClick={() => remover(slug, item.sku)}
                          className="text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1"
                        >
                          <Trash2 size={12} /> Remover
                        </button>

                        {item.quantidade >= item.estoqueDisponivel && (
                          <div className="text-[10px] text-amber-700">
                            Estoque máx.: {item.estoqueDisponivel}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Subtotal */}
                    <div className="text-right shrink-0">
                      <div className="text-lg font-semibold text-ink-900">
                        R$ {fmtPreco(item.preco * item.quantidade)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Rodapé do card */}
              <div className="border-t border-ink-100 p-4 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => {
                    if (confirm('Esvaziar carrinho?')) limpar(slug)
                  }}
                  className="text-xs text-ink-500 hover:text-red-600 inline-flex items-center gap-1"
                >
                  <Trash2 size={12} /> Esvaziar carrinho
                </button>

                <div className="text-right">
                  <div className="text-xs text-ink-500 uppercase tracking-wider">Subtotal</div>
                  <div className="text-2xl font-bold text-ink-900">R$ {fmtPreco(total)}</div>
                </div>
              </div>
            </div>

            {/* CTA Checkout */}
            <div className="mt-4 flex flex-col sm:flex-row sm:justify-end gap-2">
              <button
                type="button"
                onClick={() => navigate(`/loja/${slug}/checkout`)}
                className="btn-primary sm:min-w-[220px] justify-center"
              >
                Finalizar pedido <ArrowRight size={14} />
              </button>
            </div>

            {(info?.permite_entrega || info?.permite_retirada) && (
              <div className="text-xs text-ink-500 text-center mt-3">
                Frete e forma de pagamento calculados na próxima etapa.
              </div>
            )}
          </>
        )}
      </div>
    </LojaLayout>
  )
}
